<script setup lang="ts">
import { FALLBACK_DOWNLOAD_LINKS, getAppDownloadLinks, normalizeDownloadLinks } from '~/api/download'
import { getNewsDetail, getNewsDetailStatement } from '~/api/news'
import { normalizeCountryCodeValue } from '~/stores/app'
definePageMeta({
  name: 'news-detail',
  title: 'news-detail',
  layout: 'default',
  hideDefaultTopNav: true,
})

const appStore = useAppStore()
const route = useRoute()
const { locale, localePath, t } = useAppI18n()
const { setBreadcrumb } = useLsBreadcrumb()
const countryCodeCookie = useCookie<string>('CountryCode')

// 统一处理路由和接口里的可空文本，避免 slug、标题等入口出现 undefined 字符串。
const normalizeApiText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())
const normalizeCountryCodeText = (value: unknown) => normalizeCountryCodeValue(normalizeApiText(value))

// Nuxt 动态路由参数可能是数组，这里只取首个 slug 作为新闻详情查询键。
const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return normalizeApiText(value[0])
  }

  return normalizeApiText(value)
}

const slug = computed(() => {
  return getFirstRouteValue(route.params.slug)
})

const localeCode = computed(() => {
  return locale || 'zh-cn'
})

const catalogTitle = computed(() => {
  const normalizedLocale = localeCode.value.toLowerCase()
  return normalizedLocale === 'zh-cn' || normalizedLocale === 'zh' ? t('84087') : 'Catalog'
})

const appendImgAttr = (imgTag: string, attr: string) => {
  const [name] = attr.split('=')
  return new RegExp(`\\s${name}=`, 'i').test(imgTag) ? imgTag : imgTag.replace('<img', `<img ${attr}`)
}

type ArticleAssociation = {
  code?: string
  relationType?: number
  localShortName?: string
  englishShortName?: string
  trader?: {
    code?: string
    alias?: string
    traderCode?: string
    logo?: string
    ico?: string
    localShortName?: string
    shortName?: string
    englishShortName?: string
    score?: number
    annotation?: string
    color?: string
    labels?: Array<{
      type?: number
      Data?: Array<{
        labelName?: string
      }>
      data?: Array<{
        labelName?: string
      }>
    }>
    sortedLabels?: Array<{
      content?: string
      fontColor?: string
    }>
  }
  aggData?: {
    subject?: {
      code?: string
      logo?: string
      ico?: string
      localName?: string
      shortName?: string
      englishShortName?: string
      score?: number
      annotation?: string
    }
  }
}

type DetailStatementResponse = {
  wikicommunity?: {
    detailstatement?: {
      data?: {
        title?: string | null
        content?: string | null
        url?: string | null
      }
    }
  }
}

type DetailStatementData = {
  title?: string | null
  content?: string | null
  url?: string | null
}

type ArticleNavArticle = {
  code?: string
  title?: string
  urlPath?: string
}

type ArticleInfo = {
  title?: string
  summary?: string
  content?: string
  wikiTimestamp?: number
  time?: string
  icon?: string
  tag?: string
  associations?: ArticleAssociation[]
  prevArticle?: ArticleNavArticle | null
  nextArticle?: ArticleNavArticle | null
}

type NewsDetailBffArticleNav = {
  title?: string
  urlPath?: string
}

type NewsDetailBffAssociation = {
  code?: string
  relationType?: number
  localShortName?: string
  englishShortName?: string
  trader?: {
    code?: string
    alias?: string
    traderCode?: string
    ico?: string
    localShortName?: string
    shortName?: string
    englishShortName?: string
    score?: number
    annotation?: string
    color?: string
    labels?: Array<{
      type?: number
      Data?: Array<{
        labelName?: string
      }>
      data?: Array<{
        labelName?: string
      }>
    }>
  }
}

type NewsDetailBffArticle = {
  title?: string
  summary?: string
  content?: string
  authorAvatar?: string
  authorName?: string
  wikiTimestamp?: number
  time?: string
  associations?: NewsDetailBffAssociation[]
  prevArticle?: NewsDetailBffArticleNav | null
  nextArticle?: NewsDetailBffArticleNav | null
}

type NewsDetailBffGuessYouLikeItem = {
  rank?: number
  code?: string
  alias?: string
  name?: string
  showName?: string
  logo?: string
}

type NewsDetailBffResponse = {
  article?: NewsDetailBffArticle | null
  guessYouLike?: NewsDetailBffGuessYouLikeItem[]
}

type LeaderboardItem = {
  rank: number
  code: string
  name: string
  logo: string
  href: string
}

const articleDateFormatter = new Intl.DateTimeFormat('sv-SE', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'Asia/Shanghai',
})

// 首图参与 LCP，去掉 lazy 并补高优先级属性，其他图片保持 CMS 原始策略。
const prioritizeFirstImage = (content: string) => {
  if (!content || !content.includes('<img')) return content

  return content.replace(/<img[^>]*>/i, (imgTag) => {
    let result = imgTag.replace(/\sloading=["']lazy["']/i, '')
    result = appendImgAttr(result, 'fetchpriority="high"')
    result = appendImgAttr(result, 'decoding="async"')
    return result
  })
}

// 从关联交易商中提取内链词，按短词优先复用旧站替换策略。
const getNeiLianCiFromAssociations = (associations: ArticleAssociation[]) => {
  const kvList: { txt: string; code: string }[] = []

  associations.forEach((item) => {
    if (item.localShortName && item.code) {
      kvList.push({
        txt: item.localShortName,
        code: item.code,
      })
    }

    if (item.englishShortName && item.englishShortName !== item.localShortName && item.code) {
      kvList.push({
        txt: item.englishShortName,
        code: item.code,
      })
    }
  })

  return kvList.toSorted((a, b) => a.txt.length - b.txt.length)
}

// 只替换非链接、非图片区域中的首个匹配词，防止破坏 CMS 已有 HTML。
const replaceTextToLink = (text: string, kv: { txt: string; code: string }, lang: string) => {
  if (!text.includes(kv.txt)) return text

  let temp1 = text
  let temp2 = text
  const anchorReg = /<a(.*?)<\/a>/g
  const imgReg = /<img(.*?)\/?>/g

  temp1 = temp1.replace(anchorReg, '')
  temp1 = temp1.replace(imgReg, '')

  if (!temp1.includes(kv.txt)) return text

  const linkArr = text.match(anchorReg)
  if (linkArr?.length) {
    linkArr.forEach((item, index) => {
      temp2 = temp2.replace(item, `woody_temp_replace_link_${index}`)
    })
  }

  const imgArr = text.match(imgReg)
  if (imgArr?.length) {
    imgArr.forEach((item, index) => {
      temp2 = temp2.replace(item, `woody_temp_replace_img_${index}`)
    })
  }

  if (!temp2.includes(kv.txt)) return text

  let replacedText = temp2.replace(
    kv.txt,
    `<a class="dealerLink" href="/${lang}/dealer/${kv.code}.html" target="_blank">${kv.txt}</a>`,
  )

  if (linkArr?.length) {
    linkArr.forEach((item, index) => {
      replacedText = replacedText.replace(`woody_temp_replace_link_${index}`, item)
    })
  }

  if (imgArr?.length) {
    imgArr.forEach((item, index) => {
      replacedText = replacedText.replace(`woody_temp_replace_img_${index}`, item)
    })
  }

  return replacedText
}

// 归一化详情正文：旧 embed 视频转播放占位，并为关联交易商补站内链接。
const dealArticleContent = (content: string, associations: ArticleAssociation[], lang: string) => {
  if (!content) return ''

  let normalizedContent = content

  if (normalizedContent.includes('<embed')) {
    const embeds = normalizedContent.match(/<(embed)[^>]*>/gi)

    embeds?.forEach((item) => {
      const embSrc = item
        .substring(item.indexOf(' src="') + 6)
        .split('"')[0]
        ?.trim()
      const posterSrc = item
        .substring(item.indexOf(' imgsrc="') + 9)
        .split('"')[0]
        ?.trim()

      if (embSrc?.includes('.mp4')) {
        const videoHtml = `<div class="video" videourl="${embSrc}"><div class="play"></div><img class="poster" src="${posterSrc}" /></div>`
        normalizedContent = normalizedContent.replace(item, videoHtml)
      }
    })
  }

  if (!associations.length) return normalizedContent

  const innerLinkList = getNeiLianCiFromAssociations(associations)
  const paragraphList = normalizedContent.match(/<p(.*?)<\/p>/g) || []

  innerLinkList.forEach((item) => {
    const lowerText = item.txt
    if (
      !normalizedContent.includes(lowerText) &&
      !normalizedContent.includes(lowerText.toUpperCase()) &&
      !normalizedContent.includes(lowerText.toLowerCase())
    ) {
      return
    }

    let replaceOnce = true

    paragraphList.forEach((paragraph) => {
      if (!replaceOnce) return

      if (
        !paragraph.includes(lowerText) &&
        !paragraph.includes(lowerText.toUpperCase()) &&
        !paragraph.includes(lowerText.toLowerCase())
      ) {
        return
      }

      let tempParagraph = paragraph.replace(/<a(.*?)<\/a>/g, '')
      tempParagraph = tempParagraph.replace(/<img(.*?)\/?>/g, '')

      if (
        !tempParagraph.includes(lowerText) &&
        !tempParagraph.includes(lowerText.toUpperCase()) &&
        !tempParagraph.includes(lowerText.toLowerCase())
      ) {
        return
      }

      let replaced = replaceTextToLink(normalizedContent, item, lang)

      if (replaced === normalizedContent && lowerText !== lowerText.toUpperCase()) {
        replaced = replaceTextToLink(
          normalizedContent,
          {
            ...item,
            txt: lowerText.toUpperCase(),
          },
          lang,
        )
      }

      if (replaced === normalizedContent && lowerText !== lowerText.toLowerCase()) {
        replaced = replaceTextToLink(
          normalizedContent,
          {
            ...item,
            txt: lowerText.toLowerCase(),
          },
          lang,
        )
      }

      normalizedContent = replaced
      replaceOnce = false
    })
  })

  return normalizedContent
}

// CMS 标题可能已有 class，目录锚点样式需要覆盖为当前页面的 h2/h3 风格。
const setHeadingClass = (attrs: string, className: string) => {
  if (/\sclass=["'][^"']*["']/i.test(attrs)) {
    return attrs.replace(/\sclass=["'][^"']*["']/i, ` class="${className}"`)
  }

  return `${attrs} class="${className}"`
}

// 根据 CMS 的 fxh 标记生成文章目录，并把目录插回正文占位节点。
const buildArticleCatalog = (content: string, titleText: string) => {
  if (!content || !/fxh=["']fxh["']/i.test(content)) return content

  let h2Index = 0
  let h3Index = 0
  let headingIndex = 0
  let catalogHtml = `<div class="mlBox-title"><img src="https://resources1.interface003.com/web30/2026/06/12/ccf60686-88ea-4b03-8330-e5537a967d53.png" /><span class="mlBox-title-text">${titleText}</span></div>`

  const contentWithHeadings = content.replace(
    /<h([23])\b([^>]*)\sfxh=["']fxh["']([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (headingHtml, level, beforeAttrs, afterAttrs, innerHtml) => {
      const currentIndex = headingIndex
      headingIndex += 1

      if (level === '2') {
        h3Index = 0
        h2Index += 1
        const catalogIndex = h2Index < 10 ? `0${h2Index}` : `${h2Index}`
        catalogHtml += `<div><a class="ml-a1" href="#id_${currentIndex}"><span>${catalogIndex}</span><span>${innerHtml}</span></a></div>`
      } else {
        h3Index += 1
        const subCatalogIndex = h3Index < 10 ? `0${h3Index}` : `${h3Index}`
        catalogHtml += `<div><a class="ml-a2" href="#id_${currentIndex}"><span>${subCatalogIndex}</span><span>${innerHtml}</span></a></div>`
      }

      const attrs = setHeadingClass(`${beforeAttrs || ''} fxh="fxh"${afterAttrs || ''}`, `h${level}`)

      return `<h${level}${attrs}>${innerHtml}<div class="fhx-t" id="id_${currentIndex}"></div></h${level}>`
    },
  )

  if (headingIndex === 0) return content

  if (contentWithHeadings.includes('<div id="mlBox"></div>')) {
    return contentWithHeadings.replace('<div id="mlBox"></div>', `<div id="mlBox">${catalogHtml}</div>`)
  }

  return contentWithHeadings.replace(/<h2\b/i, `<div id="mlBox">${catalogHtml}</div><h2`)
}

const currentCountryCode = computed(() => {
  return (
    normalizeCountryCodeText(appStore.CountryCode) ||
    normalizeCountryCodeText(appStore.usergetCountry?.countryCode) ||
    normalizeCountryCodeText(countryCodeCookie.value) ||
    '156'
  )
})

// 首屏缓存键仅使用路由 slug，确保 SSR payload 在客户端水合时可被同一 key 复用。
const { data: newsDetailResponse, status: newsDetailStatus, refresh: refreshNewsDetail } = await useAsyncData<
  NewsDetailBffResponse | null
>(
  () => `news-detail-${slug.value}`,
  async () => {
    if (!slug.value) {
      return null
    }

    try {
      return await getNewsDetail<NewsDetailBffResponse>(slug.value)
    } catch {
      return null
    }
  },
  {
    default: () => null,
    watch: [slug],
  },
)

// 国家或语言仅在水合完成后触发刷新，避免首屏两端因上下文变化走到不同渲染分支。
onMounted(() => {
  watch([localeCode, currentCountryCode], () => {
    void refreshNewsDetail()
  })
})
// BFF 字段在这里收敛为页面内部 camelCase 结构，模板只消费稳定字段。
const articleInfo = computed<ArticleInfo>(() => {
  const article = newsDetailResponse.value?.article
  if (article?.title || article?.content) {
    return {
      title: article.title,
      summary: article.summary,
      content: article.content,
      wikiTimestamp: article.wikiTimestamp,
      time: article.time,
      icon: article.authorAvatar,
      tag: article.authorName,
      associations: Array.isArray(article.associations)
        ? article.associations.map((item) => ({
            code: item.code,
            relationType: item.relationType,
            localShortName: item.localShortName,
            englishShortName: item.englishShortName,
            trader: item.trader
              ? {
                  code: item.trader.code,
                  alias: item.trader.alias,
                  traderCode: item.trader.traderCode,
                  ico: item.trader.ico,
                  localShortName: item.trader.localShortName,
                  shortName: item.trader.shortName,
                  englishShortName: item.trader.englishShortName,
                  score: item.trader.score,
                  annotation: item.trader.annotation,
                  color: item.trader.color,
                  labels: item.trader.labels,
                }
              : undefined,
          }))
        : [],
      prevArticle: article.prevArticle
        ? {
            title: article.prevArticle.title,
            urlPath: article.prevArticle.urlPath,
          }
        : null,
      nextArticle: article.nextArticle
        ? {
            title: article.nextArticle.title,
            urlPath: article.nextArticle.urlPath,
          }
        : null,
    }
  }

  return {}
})
const showArticleEmptyState = computed(() => {
  return newsDetailStatus.value !== 'pending' && !newsDetailResponse.value?.article
})

// 正文相关派生数据保持集中，避免模板直接访问接口嵌套结构。
const articleAssociations = computed<ArticleAssociation[]>(() => {
  const associations = articleInfo.value?.associations
  return Array.isArray(associations) ? associations : []
})
const titleText = computed(() => articleInfo.value?.title?.trim() || '')
const breadcrumbItems = computed(() => [
  {
    label: t('82520'),
    route: '/',
  },
  {
    label: t('84041'),
  },
])

/** 跳转交易所详情前缓存当前新闻详情页，供交易所详情还原来源面包屑。 */
const setExchangeDetailBreadcrumb = (exchangeAlias: string) => {
  setBreadcrumb(exchangeAlias, {
    labelKey: '84041',
    route: route.fullPath,
  })
}
const authorAvatar = computed(() => articleInfo.value?.icon?.trim())
const authorName = computed(() => articleInfo.value?.tag?.trim() || '')
const summaryText = computed(() => t('84044') + (articleInfo.value?.summary?.trim() || ''))
// TDK 描述优先使用文章摘要；摘要缺失时，从正文纯文本中截取前 120 个字符。
const seoDescription = computed(() => {
  const articleSummary = articleInfo.value?.summary?.trim()
  const articleExcerpt = articleSummary || normalizeApiText(articleInfo.value?.content).replace(/<[^>]*>/g, ' ')
  const normalizedExcerpt = articleExcerpt.replace(/\s+/g, ' ').trim().slice(0, 120)

  return t('84384', [normalizedExcerpt])
})

// 输出给 v-html 的最终正文，只在这里做 HTML 改写与首图优先级处理。
const articleContentHtml = computed(() => {
  const rawContent = articleInfo.value?.content?.trim() || ''
  if (!rawContent) return ''

  let normalizedContent = rawContent
  normalizedContent = normalizedContent.replace(/<p>\s+/g, '<p>')

  normalizedContent = dealArticleContent(normalizedContent, articleAssociations.value, localeCode.value)
  if (normalizedContent.includes('fxh="fxh"') && !normalizedContent.includes('<div id="mlBox"></div>')) {
    normalizedContent = normalizedContent.replace('<h2', '<div id="mlBox"></div><h2')
  }

  normalizedContent = buildArticleCatalog(normalizedContent, catalogTitle.value)
  normalizedContent = prioritizeFirstImage(normalizedContent)

  return normalizedContent
})
const activeVideoUrl = ref('')
const isVideoDialogOpen = computed(() => Boolean(activeVideoUrl.value))
const videoDialogMask = ref<HTMLElement | null>(null)

// 关闭弹窗时只清空视频地址，让 computed 统一控制弹窗显隐。
const closeVideoDialog = () => {
  activeVideoUrl.value = ''
}

// 正文视频由 CMS HTML 代理点击，事件委托可避免给 v-html 内容手动绑定监听。
const handleArticleContentClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null
  if (!target) return

  const isVideoTrigger = target.classList.contains('play') || target.classList.contains('poster')
  if (!isVideoTrigger) return

  const videoContainer = target.closest('.video')
  const videoUrl = videoContainer?.getAttribute('videourl')
  if (!videoUrl) return

  activeVideoUrl.value = videoUrl
}

// 优先使用接口时间戳，缺失时回退到字符串时间，展示格式固定为 yyyy-MM-dd。
const articleDateText = computed(() => {
  const timestamp = articleInfo.value?.wikiTimestamp
  if (typeof timestamp === 'number' && Number.isFinite(timestamp)) {
    return articleDateFormatter.format(timestamp)
  }

  const time = articleInfo.value?.time
  if (typeof time === 'string' && time.trim()) {
    const date = new Date(time)
    if (!Number.isNaN(date.getTime())) {
      return articleDateFormatter.format(date)
    }
  }

  return ''
})

// 免责声明来自旧聚合接口，按语言独立缓存，失败时不阻塞正文渲染。
const { data: detailStatementResponse } = await useAsyncData<DetailStatementData | null>(
  () => `news-detail-statement-${localeCode.value}`,
  async () => {
    try {
      const response = await getNewsDetailStatement<DetailStatementResponse>()

      return response?.wikicommunity?.detailstatement?.data || null
    } catch {
      return null
    }
  },
  {
    default: () => null,
    watch: [localeCode],
  },
)
// 右侧下载卡片与实勘列表页共用 App 下载接口，接口异常时保持默认下载地址可用。
const { data: downloadLinks } = await useAsyncData(
  'news-detail-download-links',
  async () => {
    return normalizeDownloadLinks(await getAppDownloadLinks().catch(() => null))
  },
  {
    default: () => normalizeDownloadLinks(FALLBACK_DOWNLOAD_LINKS),
  },
)
// 视频弹窗打开时锁定页面滚动，并把焦点移到 mask 以支持 Esc 关闭。
watch(isVideoDialogOpen, (visible) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = visible ? 'hidden' : ''

  if (visible) {
    nextTick(() => {
      videoDialogMask.value?.focus()
    })
  }
})

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = ''
})
const disclaimerTitle = computed(() => detailStatementResponse.value?.title?.trim() || '')
const disclaimerText = computed(() => detailStatementResponse.value?.content?.trim() || '')
// 关联交易商卡片只展示有 code 的项，评分和年限标签在这里做展示格式化。
const featuredTraders = computed(() => {
  return articleAssociations.value
    .map((item) => {
      const trader = item?.trader
      if (!trader?.code) {
        return null
      }

      const labels = Array.isArray(trader.labels) ? trader.labels : []
      const yearsLabel =
        labels.find((label) => label.type === 101)?.Data?.[0]?.labelName ||
        labels.find((label) => label.type === 101)?.data?.[0]?.labelName ||
        ''

      return {
        key: `${item.relationType || 0}-${trader.code}`,
        code: trader.code || trader.traderCode || '',
        alias: trader.alias || '',
        logo: trader.ico || '',
        name: trader.localShortName || trader.shortName || trader.englishShortName || '',
        status: trader.annotation || '',
        statusColor: trader.color,
        years: yearsLabel,
        score: typeof trader.score === 'number' ? trader.score.toFixed(2) : '',
      }
    })
    .filter(
      (
        item,
      ): item is {
        key: string
        code: string
        alias: string
        logo: string
        name: string
        status: string
        statusColor?: string
        years: string
        score: string
      } => Boolean(item?.code),
    )
})

// 上一篇/下一篇链接统一在脚本中生成 localePath，模板只负责渲染卡片。
const articleNavigationCards = computed(() => {
  const prevArticle = articleInfo.value?.prevArticle
  const nextArticle = articleInfo.value?.nextArticle

  return [
    {
      key: 'prev',
      label: t('83780'),
      title: prevArticle?.title?.trim() || '',
      href: prevArticle?.urlPath ? localePath({ name: 'news-detail', params: { slug: prevArticle.urlPath } }) : '',
      align: 'left',
    },
    {
      key: 'next',
      label: t('83781'),
      title: nextArticle?.title?.trim() || '',
      href: nextArticle?.urlPath ? localePath({ name: 'news-detail', params: { slug: nextArticle.urlPath } }) : '',
      align: 'right',
    },
  ].filter((item) => item.title && item.href)
})

// 右侧榜单最多展示 10 条，接口缺 rank 时按当前排序补位。
const leaderboardItems = computed<LeaderboardItem[]>(() => {
  const list = newsDetailResponse.value?.guessYouLike

  if (!Array.isArray(list)) {
    return []
  }

  return list
    .slice(0, 10)
    .map((item, index) => {
      const code = item.code || ''
      const alias = item.alias || ''
      const name = item.name || ''

      return {
        rank: typeof item.rank === 'number' ? item.rank : index + 1,
        code,
        alias,
        name,
        logo: item.logo || '',
        href: localePath({ name: 'exchange-detail', params: { slug: alias } }),
      }
    })
    .filter((item) => item.alias && item.name)
})

useHead(() => ({
  title: seoDescription.value,
  meta: seoDescription.value
    ? [
        {
          name: 'description',
          content: seoDescription.value,
        },
      ]
    : [],
}))
</script>

<template>
  <HeaderWikiBitPcTopNav :always-expand-search="true" :force-solid-background="true" />
  <main class="min-h-screen bg-[#f5f6f8] text-[#292a2e]">
    <section class="pt-[94px] max-[799px]:pt-[86px]">
      <div class="mx-auto w-[min(100%,1200px)] max-[1240px]:mx-[24px] max-[1240px]:w-auto max-[799px]:mx-[16px]">
        <CommonBreadcrumb :items="breadcrumbItems" />
      </div>
    </section>
    <section v-if="showArticleEmptyState" class="news-empty-state">
      <div class="news-empty-state-illustration" aria-hidden="true">
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/06/23/48f3e57b-f22a-4188-b631-841f981d0b19.png"
          alt=""
          loading="eager"
        />
      </div>
      <div class="news-empty-state-content">
        <p class="news-empty-state-title">
          {{ t('84024') }}
        </p>
        <div class="news-empty-state-note">
          <p>
            {{ t('84025') }}
          </p>
          <p>
            1.{{ t('84031') }}<br />
            2.{{ t('84032') }}<br />
            3.{{ t('84033') }}<br />
            4.{{ t('84036') }}<br />
            5.{{ t('84035') }}
          </p>
          <p>
            {{ t('84037') }}
          </p>
        </div>
      </div>
      <NuxtLink
        :to="localePath({ name: 'home' })"
        class="news-empty-back news-empty-back-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t('84038') }}
      </NuxtLink>
    </section>
    <section
      v-else
      class="mx-auto grid w-[min(100%,1200px)] grid-cols-[minmax(0,792px)_384px] items-start gap-[24px] pb-[64px] max-[1240px]:mx-[24px] max-[1240px]:w-auto max-[1240px]:grid-cols-[minmax(0,1fr)_clamp(280px,32.67%,384px)] max-[799px]:mx-[16px] max-[799px]:grid-cols-[minmax(0,1fr)] max-[799px]:gap-[16px]"
    >
      <article
        class="flex flex-col gap-[16px] rounded-[12px] bg-white p-[24px] max-[799px]:px-[16px] max-[799px]:py-[20px]"
        :class="{
          'bg-transparent': showArticleEmptyState,
        }"
      >
        <header class="news-article-header">
          <div class="news-article-title-group">
            <h1 class="news-article-title">
              {{ titleText }}
            </h1>

            <div class="news-article-meta-row">
              <div class="news-article-author-group">
                <CommonImage
                  :src="authorAvatar"
                  :alt="authorName || 'author'"
                  class="news-article-author-avatar"
                  object-fit="cover"
                  loading="eager"
                />
                <span class="news-article-author-name">{{ authorName }}</span>
                <span class="news-article-author-divider">|</span>
                <time class="news-article-date">{{ articleDateText }}</time>
              </div>

              <CommonArticleShareGroup :title="titleText" />
            </div>
          </div>

          <div class="news-article-summary">
            <p>{{ summaryText }}</p>
          </div>
        </header>

        <div
          id="articleInfo"
          v-if="articleContentHtml"
          class="news-article-content news-article-content-html"
          @click="handleArticleContentClick"
          v-html="articleContentHtml"
        />

        <section v-if="disclaimerText" class="news-article-disclaimer">
          <h2>{{ disclaimerTitle }}</h2>
          <div v-html="disclaimerText"></div>
        </section>

        <section
          v-if="articleNavigationCards.length > 0"
          class="news-article-navigation"
          :class="{
            'news-article-navigation-single': articleNavigationCards.length === 1,
          }"
        >
          <NuxtLink
            v-for="item in articleNavigationCards"
            :key="item.key"
            :to="item.href"
            class="news-article-nav-card"
            target="_blank"
            rel="noopener noreferrer"
            :class="[
              `news-article-nav-card-${item.align}`,
              {
                'news-article-nav-card-single': articleNavigationCards.length === 1,
              },
            ]"
          >
            <span class="news-article-nav-label">{{ item.label }}</span>
            <p class="news-article-nav-title">
              {{ item.title }}
            </p>
          </NuxtLink>
        </section>

        <NuxtLink
          v-for="item in featuredTraders"
          :key="item.key"
          :to="localePath({ name: 'exchange-detail', params: { slug: item.alias } })"
          class="news-featured-trader"
          target="_blank"
          rel="noopener noreferrer"
          @click="setExchangeDetailBreadcrumb(item.alias)"
        >
          <div class="news-featured-trader-main">
            <CommonImage
              :src="item.logo"
              :alt="item.name"
              class="news-featured-trader-logo"
              object-fit="cover"
              loading="eager"
            />
            <span class="news-featured-trader-name">{{ item.name }}</span>
          </div>
          <span class="news-featured-trader-status" :style="{ color: item.statusColor }">
            {{ item.status }}
          </span>
          <span class="news-featured-trader-years">{{ item.years }}</span>
          <span class="news-featured-trader-score">
            <CommonImage
              src="https://resources1.interface003.com/web30/2026/06/22/6837e2f3-6809-462c-be7d-a9df7658963e.png"
              alt="score"
              class="news-featured-trader-score-icon"
              object-fit="cover"
              loading="eager"
            />
            {{ item.score }}</span
          >
        </NuxtLink>
      </article>

      <aside class="flex min-w-[280px] flex-col gap-[20px] max-[1240px]:min-w-[280px] max-[799px]:min-w-0">
        <section
          v-if="leaderboardItems.length > 0"
          class="rounded-[12px] border-[0.965px] border-[rgba(5,21,36,0.06)] bg-white px-[24px] pt-[8px] pb-[16px] max-[799px]:px-[16px]"
        >
          <div class="news-leaderboard-header">
            <h2>{{ t('84042') }}</h2>
          </div>

          <div class="news-leaderboard-list">
            <NuxtLink
              v-for="item in leaderboardItems"
              :key="`${item.rank}-${item.name}`"
              class="news-leaderboard-item"
              :to="item.href"
              target="_blank"
              rel="noopener noreferrer"
              @click="setExchangeDetailBreadcrumb(item.alias)"
            >
              <span class="news-leaderboard-rank">{{ item.rank }}</span>
              <CommonImage
                :src="item.logo"
                :alt="item.name"
                class="news-leaderboard-logo"
                object-fit="cover"
                loading="eager"
              />
              <span class="news-leaderboard-name">{{ item.name }}</span>
            </NuxtLink>
          </div>
        </section>

        <div class="sticky top-[94px] max-[799px]:static">
          <CommonDownload :download-links="downloadLinks" class="min-w-0 max-[1240px]:min-w-0" />
        </div>
      </aside>
    </section>

    <Transition name="news-video-dialog-fade">
      <section
        v-if="isVideoDialogOpen"
        ref="videoDialogMask"
        class="news-video-dialog-mask"
        role="dialog"
        aria-modal="true"
        aria-label="Video player"
        tabindex="-1"
        @click.self="closeVideoDialog"
        @keydown.esc="closeVideoDialog"
      >
        <div class="news-video-dialog">
          <button
            type="button"
            class="news-video-dialog-close"
            aria-label="Close video dialog"
            @click="closeVideoDialog"
          >
            <span class="news-video-dialog-close-mark"></span>
          </button>

          <div class="news-video-dialog-body">
            <video
              v-if="activeVideoUrl"
              class="news-video-dialog-player"
              :src="activeVideoUrl"
              controls
              autoplay
              playsinline
            ></video>
          </div>
        </div>
      </section>
    </Transition>
  </main>
</template>

<style scoped lang="less">
.news-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 620px;
  padding: 32px 0;
}

.news-empty-state-illustration {
  width: 230px;
  height: 132px;
  img {
    width: 230px;
    height: 132px;
  }
}

.news-empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.news-empty-state-title {
  max-width: 560px;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #292a2e;
  text-align: center;
}

.news-empty-state-note {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 570px;
  padding: 12px;
  border-radius: 12px;
  background: #f0f1f2;
}

.news-empty-state-note p {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #7d818a;
}
.news-empty-back {
  display: flex;
  width: 156px;
  height: 46px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  background: #112d73;
  color: #fff;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 46px;
  cursor: pointer;
  text-decoration: none;
}

.news-article-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.news-article-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #292a2e;
}

.news-article-meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
}

.news-article-author-group,
.news-featured-trader-main,
.news-featured-trader-score,
.news-leaderboard-item {
  display: flex;
  align-items: center;
}

.news-article-author-group {
  gap: 8px;
}

.news-article-author-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.news-article-author-name {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.news-article-author-divider {
  color: #7d818a;
}

.news-article-date {
  font-size: 11px;
  line-height: 16px;
  color: #8c8f97;
}

.news-article-summary,
.news-article-disclaimer {
  padding: 20px;
  border-radius: 12px;
  background: #f8f8f8;
}

.news-article-summary p {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: #505258;
}

.news-article-disclaimer p {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #505258;
}

.news-article-disclaimer h2,
.news-leaderboard-header h2 {
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #292a2e;
}

.news-article-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.news-article-content-html :deep(*) {
  max-width: 100%;
  box-sizing: border-box;
}

.news-article-content-html :deep(#mlBox) {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
  border-radius: 12px;
  background: #f8f8f8;
  box-shadow: none;
}

.news-article-content-html :deep(.mlBox-title) {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
}

.news-article-content-html :deep(.mlBox-title img) {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin: 0 !important;
  min-height: auto !important;
}

.news-article-content-html :deep(.mlBox-title-text) {
  padding: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #505258;
}

.news-article-content-html :deep(.ml-a1),
.news-article-content-html :deep(.ml-a2) {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.news-article-content-html :deep(.ml-a1) {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #505258;
}

.news-article-content-html :deep(.ml-a2) {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #7d818a;
}

.news-article-content-html :deep(.ml-a1 span),
.news-article-content-html :deep(.ml-a2 span) {
  flex-shrink: 0;
}

.news-article-content-html :deep(.ml-a2 span:first-child) {
  min-width: 16px;
}

.news-article-content-html :deep(.ml-a1 span:last-child),
.news-article-content-html :deep(.ml-a2 span:last-child) {
  flex-shrink: 1;
  min-width: 0;
  white-space: normal;
}

.news-article-content-html :deep(.ch2),
.news-article-content-html :deep(.ch3) {
  margin: 0 10px;
}

.news-article-content-html :deep(p),
.news-article-content-html :deep(li) {
  font-size: 14px;
  line-height: 22px;
  color: #292a2e;
}

.news-article-content-html :deep(p) {
  margin: 0;
}

.news-article-content-html :deep(img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 16px auto;
}

.news-article-content-html :deep(.h2),
.news-article-content-html :deep(h2) {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  font-size: 20px !important;
  font-weight: 600 !important;
  line-height: 28px;
  color: #292a2e;
}

.news-article-content-html :deep(.h3),
.news-article-content-html :deep(h3) {
  display: block;
  padding: 0;
  border: 0;
  font-size: 18px !important;
  font-weight: 600 !important;
  line-height: 26px;
  color: #292a2e;
}

.news-article-content-html :deep(h4) {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #292a2e;
}

.news-article-content-html :deep(h5) {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #292a2e;
}

.news-article-content-html :deep(.fhx-t) {
  transform: translateY(-172px);
}

.news-article-content-html :deep(ol),
.news-article-content-html :deep(ul) {
  padding-left: 21px;
  color: #292a2e;
}

.news-article-content-html :deep(ol) {
  list-style-type: decimal;
  list-style-position: outside;
}

.news-article-content-html :deep(ul) {
  list-style-type: disc;
  list-style-position: outside;
}

.news-article-content-html :deep(ol > li) {
  list-style: decimal;
  list-style-position: outside;
}

.news-article-content-html :deep(ul > li) {
  list-style: disc;
  list-style-position: outside;
}

.news-article-content-html :deep(ol li),
.news-article-content-html :deep(ul li) {
  display: list-item;
  margin-bottom: 14px;
  padding-left: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: #292a2e;
}

.news-article-content-html :deep(ol li:last-child),
.news-article-content-html :deep(ul li:last-child) {
  margin-bottom: 0;
}

.news-article-content-html :deep(ol li p),
.news-article-content-html :deep(ul li p) {
  margin: 0 !important;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #292a2e;
}

.news-article-content-html :deep(table),
.news-article-content-html :deep(.cms_autoformat_table) {
  width: 100% !important;
  table-layout: fixed;
  border-collapse: collapse;
  background: #fff;
  line-height: 18px;
  margin: 16px 0;
}

.news-article-content-html :deep(td),
.news-article-content-html :deep(th) {
  padding: 12px;
  border: 1px solid #d1d1d1;
  word-break: break-word;
  text-align: left;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #7d818a;
}

.news-article-content-html :deep(tr > td:first-child),
.news-article-content-html :deep(tr > th:first-child) {
  width: 136px;
  min-width: 136px;
  max-width: 136px;
  padding: 12px;
  vertical-align: middle;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #7d818a;
}

.news-article-content-html :deep(tr > td b),
.news-article-content-html :deep(tr > th b) {
  font-weight: 500;
}

.news-article-content-html :deep(tr > td:nth-child(2)),
.news-article-content-html :deep(tr > th:nth-child(2)) {
  padding: 12px;
  vertical-align: middle;
  text-align: left;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #7d818a;
}

.news-article-content-html :deep(td p),
.news-article-content-html :deep(th p) {
  font-size: 16px;
  line-height: 18px;
  color: #3d3d3d;
}

.news-article-content-html :deep(tr > td:first-child p),
.news-article-content-html :deep(tr > th:first-child p) {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #7d818a;
  text-align: left;
}

.news-article-content-html :deep(tr > td:nth-child(2) p),
.news-article-content-html :deep(tr > th:nth-child(2) p) {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #7d818a;
  text-align: left;
}

.news-article-content-html :deep(td img),
.news-article-content-html :deep(th img) {
  display: block;
  margin: 0 auto;
}

.news-article-content-html :deep(center) {
  display: block;
  text-align: center;
}

.news-article-content-html :deep(a) {
  color: #0d3f63;
  text-decoration: underline;
}

.news-article-content-html :deep(a:hover) {
  text-decoration: underline;
}

.news-article-content-html :deep(.ml-a2:hover),
.news-article-content-html :deep(.ml-a1:hover) {
  text-decoration: none;
}

.news-article-content-html :deep(.dealerLink) {
  color: #0d3f63;
}

.news-article-content-html :deep(.video) {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  margin: 24px 0;
  display: flex;
  justify-content: center;
}

.news-article-content-html :deep(.video .play) {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 57px !important;
  height: 57px !important;
  transform: translate(-50%, -50%);
  background: url('https://resources1.interface003.com/web20/img/othor/video_btn.png') no-repeat;
  background-size: contain;
}

.news-article-content-html :deep(.video video) {
  height: 100%;
  background: #000;
}

.news-article-content-html :deep(.video .poster) {
  max-width: 100%;
}

.news-article-paragraph {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #292a2e;
}

.news-article-cover {
  width: 100%;
  height: auto;
  display: block;
}

.news-article-disclaimer {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
  font-size: 14px;
  color: #505258;
  line-height: 22px;
}

.news-article-disclaimer h2 {
  margin: 0;
  font-weight: 500;
  color: #292a2e;
}

.news-article-navigation {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
  margin-top: 4px;
}

.news-article-navigation-single {
  grid-template-columns: minmax(0, 1fr);
}

.news-article-nav-card {
  padding: 20px;
  background: #fff;
  cursor: pointer;
}

.news-article-nav-card-left {
  border-radius: 12px;
  background: #fff;
}

.news-article-nav-card:hover {
  background: #f8f8f8;
  .news-article-nav-title {
    color: #292a2e;
  }
}

.news-article-nav-card-right {
  border-radius: 0 12px 12px 0;
  text-align: right;
}

.news-article-nav-label {
  display: block;
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 22px;
  color: #7d818a;
}

.news-article-nav-title {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: #7d818a;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.news-article-nav-card-right {
  border-radius: 12px;
  .news-article-nav-title {
    text-align: right;
  }
}

.news-article-nav-card-single {
  border-radius: 12px;
  text-align: left;
  .news-article-nav-title {
    text-align: left;
  }
}

.news-featured-trader {
  display: grid;
  grid-template-columns:
    minmax(0, 1.2fr) minmax(0, 1fr)
    minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  margin-top: 12px;
}

.news-featured-trader:hover {
  background: #f8f8f8;
}

.news-featured-trader-main {
  gap: 8px;
  min-width: 0;
}

.news-featured-trader-logo {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  background: #ccc;
  overflow: hidden;
}

.news-featured-trader-name,
.news-featured-trader-status,
.news-featured-trader-years,
.news-featured-trader-score {
  font-size: 14px;
  line-height: 22px;
}

.news-featured-trader-name {
  font-weight: 500;
  color: #292a2e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news-featured-trader-status {
  text-align: center;
}

.news-featured-trader-years {
  text-align: center;
  color: #292a2e;
}

.news-featured-trader-score {
  gap: 4px;
  justify-self: end;
  padding: 4px 12px;
  border-radius: 999px;
  background: #f8f8f8;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
}

.news-featured-trader-score :deep(svg) {
  width: 14px;
  height: 14px;
}

.news-featured-trader-score-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.news-video-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.72);
}

.news-video-dialog {
  position: relative;
  width: 858px;
  height: 483px;
  background: #000;
  display: flex;
  justify-content: center;
  align-items: center;
}

.news-video-dialog-body {
  width: 100%;
  height: 100%;
}

.news-video-dialog-player {
  display: block;
  width: 100%;
  height: 100%;
  background: #000;
  object-fit: contain;
}

.news-video-dialog-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  transition: background-color 0.2s ease;
}

.news-video-dialog-close:hover {
  background: rgba(255, 255, 255, 0.24);
}

.news-video-dialog-close-mark {
  position: relative;
  display: block;
  width: 14px;
  height: 14px;
  margin: 0 auto;
}

.news-video-dialog-close-mark::before,
.news-video-dialog-close-mark::after {
  content: '';
  position: absolute;
  top: 6px;
  left: 0;
  width: 14px;
  height: 2px;
  border-radius: 999px;
  background: #fff;
}

.news-video-dialog-close-mark::before {
  transform: rotate(45deg);
}

.news-video-dialog-close-mark::after {
  transform: rotate(-45deg);
}

.news-video-dialog-fade-enter-active,
.news-video-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.news-video-dialog-fade-enter-active .news-video-dialog,
.news-video-dialog-fade-leave-active .news-video-dialog {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.news-video-dialog-fade-enter-from,
.news-video-dialog-fade-leave-to {
  opacity: 0;
}

.news-video-dialog-fade-enter-from .news-video-dialog,
.news-video-dialog-fade-leave-to .news-video-dialog {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

.news-leaderboard-header {
  padding: 16px 0;
  border-bottom: 0.482px solid rgba(5, 21, 36, 0.06);
}

.news-leaderboard-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #292a2e;
}

.news-leaderboard-list {
  padding-top: 12px;
}

.news-leaderboard-item {
  display: flex;
  gap: 8px;
  min-height: 44px;
  min-width: 0;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
}

.news-leaderboard-item:hover .news-leaderboard-name {
  color: #112d73;
}

.news-leaderboard-rank {
  width: 20px;
  text-align: center;
  font-size: 14px;
  line-height: 22px;
  color: #b7b9be;
  flex-shrink: 0;
}

.news-leaderboard-item:nth-child(-n + 3) .news-leaderboard-rank {
  color: #292a2e;
}

.news-leaderboard-logo {
  width: 20px;
  height: 20px;
  border-radius: 2px;
  object-fit: cover;
  flex-shrink: 0;
}

.news-leaderboard-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  line-height: 23px;
  color: #292a2e;
}

@media (max-width: 799px) {
  .news-video-dialog-mask {
    padding: 12px;
  }

  .news-video-dialog-body {
    padding: 16px;
  }

  .news-video-dialog-player {
    max-height: 70vh;
  }

  .news-empty-state {
    min-height: 520px;
    padding: 12px 0;
  }

  .news-empty-state-title {
    max-width: 100%;
  }

  .news-empty-state-note {
    width: 100%;
  }

  .news-article-title {
    font-size: 24px;
  }

  .news-article-meta-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .news-article-navigation {
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
  }

  .news-article-nav-card-left,
  .news-article-nav-card-right {
    border-radius: 12px;
    text-align: left;
  }

  .news-featured-trader {
    grid-template-columns: minmax(0, 1fr);
    justify-items: start;
  }

  .news-featured-trader-status,
  .news-featured-trader-years,
  .news-featured-trader-score {
    text-align: left;
    justify-self: start;
  }
}
</style>
